/* eslint-disable camelcase */
import React from 'react';
import { withStyles } from '@material-ui/core';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import { useDispatch } from 'react-redux';
import { DispatchDragNDropTypes, DispatchStatus, padNumber } from '~/utils';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import styles from '../styles';
import PF2MEquipIcon from '~/components/PF2MEquipIcon';
import PF2MDispatchIcon from '~/components/PF2MDispatchIcon';
import PF2MTooltip from '~/components/PF2MTooltip';
import {
  bulkUpdateDynamic,
  getLoadMaterialMappings,
  setLoadAllocationPriority,
  setLoadMaterialMappingsItem,
  setSelectedLoadAllocation,
  setSelectedTruckAllocation,
  updateIsTruckAllocationLoading,
} from '~/store/dispatch/actions';
import moduleIcons from '~/utils/modulesIcons';
import PF2MIconButton from '~/components/PF2MIconButton';
import PF2MSwitch from '~/components/PF2MSwitch';


const AllocationRow = ({
  loadAllocation,
  loadAllocations,
  trucks,
  isFirst,
  isLast,
  onTruckDrop,
  onLoadDrop,
  classes,
  openLoadModal,
  openTruckModal,
  isFullscreen,
  dataTrucksOrigins,
  dataExcavatorOrigins,
  openUpdateDynamicAllocationModal,
  isDynamicEnable,
  isOverload,
}) => {
  const dispatch = useDispatch();
  const isDynamicAllocationEnabled = Boolean(loadAllocation.dynamic_allocation);
  const isDynamicSwitchDisabled = Boolean(loadAllocation?.dynamic_configuration) === false;

  const getLoadMaterialMappingsCallback = monitor => (response) => {
    const foundLoadMaterialMapping = response
      .filter(r => r.load_allocation_id
        && r.load_allocation_id === loadAllocation.id)
      .map(r => r.material_id);

    const newLoadMaterialMappingItems = response.map(m => ({
      ...m,
      equip_id: monitor.obj.equip_id,
      equip_name: monitor.obj.equip_name,
      update_status: 'I',
      id: null,
    }));

    newLoadMaterialMappingItems.forEach((m) => {
      dispatch(setLoadMaterialMappingsItem(m));
    });

    const newLoadAllocation = {
      ...loadAllocation,
      equip_id: monitor.obj.equip_id,
      equip_name: monitor.obj.equip_name,
      materials: foundLoadMaterialMapping,
    };

    onLoadDrop(
      newLoadAllocation,
      newLoadMaterialMappingItems.filter(
        r => newLoadAllocation.materials.includes(r.material_id),
      ),
      dataTrucksOrigins.map(r => r.equip_id),
    );
  };

  const [, loadDrop] = useDrop({
    accept: DispatchDragNDropTypes.LOAD,
    drop: (monitor) => {
      if (loadAllocation.equip_id !== monitor.obj.equip_id) {
        dispatch(updateIsTruckAllocationLoading(true));
        dispatch(getLoadMaterialMappings(
          { equipmentId: loadAllocation.equip_id },
          getLoadMaterialMappingsCallback(monitor),
        ));
      }
    },
    collect: monitor => ({
      isLoadOver: !!monitor.isOver(),
    }),
  });

  const [{ dragItem }, truckDrop] = useDrop({
    accept: DispatchDragNDropTypes.TRUCK,
    drop: (monitor) => {
      if (loadAllocation.id !== monitor.obj.load_allocation_id) {
        onTruckDrop(monitor.obj, loadAllocation.id);
      }
    },
    collect: monitor => ({
      dragItem: monitor.getItem(),
    }),
  });

  return (
    <tr
      key={loadAllocation.id}
      style={{
        display: 'table-row',
        width: '100%',
        height: 'auto',
      }}
    >
      {
        isFullscreen ? null : (
          <>
            <td
              style={{
                display: 'table-cell',
                alignItems: 'center',
                paddingInline: '5px',
              }}
            >
              <div style={{ display: 'flex' }}>
                <div style={{
                  cursor: 'pointer',
                  width: '50%',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                >
                  <ArrowDropUpIcon
                    fontSize="large"
                    style={{ cursor: 'pointer', width: '100%' }}
                    onClick={() => {
                      if (!isFirst) {
                        dispatch(setLoadAllocationPriority(
                          loadAllocation, loadAllocations, loadAllocation.priority - 1, true,
                        ));
                      }
                    }}
                  />
                  <ArrowDropDownIcon
                    fontSize="large"
                    style={{ cursor: 'pointer', width: '100%' }}
                    onClick={() => {
                      if (!isLast) {
                        dispatch(setLoadAllocationPriority(
                          loadAllocation, loadAllocations, loadAllocation.priority + 1, true,
                        ));
                      }
                    }}
                  />
                </div>
                <div
                  className={classes.labelTypeSelector}
                  style={{
                    alignItems: 'center',
                    fontSize: 40,
                  }}
                >
                  {padNumber(loadAllocation.priority, 2)}
                </div>
              </div>
            </td>

            <td
              className={classes.labelTypeSelector}
              style={{
                display: 'table-cell',
                alignItems: 'center',
                fontSize: 40,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
                <p style={{ textAlign: 'center' }}>
                  {loadAllocation.dynamic_configuration?.group_name || '-'}
                </p>
                {
                  isOverload ? (
                    <div style={{ width: '30px', height: '30px', display: 'flex' }}>
                      <moduleIcons.WarningDiamond style={{ width: '40px', height: '40px', fill: 'none' }} />
                    </div>
                  ) : (
                    <div style={{ width: '30px', height: '30px', display: 'flex' }} />
                  )
                }
              </div>
            </td>
          </>
        )
      }
      <td
        className={classes.labelTypeSelector}
        style={{
          display: 'table-cell',
          alignItems: 'center',
          fontSize: 16,
          paddingInline: 5,
        }}
      >
        <PF2MDispatchIcon
          iconType="stack"
          warnings=""
          customStyles={{
            cursor: 'default',
            height: 50,
          }}
        />
        <PF2MTooltip
          title={loadAllocation.origin_element_point_name}
          PopperProps={{ disablePortal: true }}
        >
          <span
            style={{
              padding: '2px 5px',
              borderRadius: '8px',
              backgroundColor: '#64788626',
              maxWidth: 120,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textWrap: 'nowrap',
            }}
          >
            {loadAllocation.origin_element_point_name ? loadAllocation.origin_element_point_name.slice(0, 12) : '-'}
          </span>
        </PF2MTooltip>
      </td>

      {isDynamicEnable && (
        <td
          className={classes.labelTypeSelector}
          style={{
            display: 'table-cell',
            alignItems: 'center',
            fontSize: 16,
            paddingInline: 5,
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <PF2MSwitch
              checked={!isDynamicSwitchDisabled && isDynamicAllocationEnabled}
              disabled={isDynamicSwitchDisabled}
              onChange={() => {
                dispatch(updateIsTruckAllocationLoading(true));
                dispatch(bulkUpdateDynamic({
                  payload: {
                    load_allocation_ids: [loadAllocation.id],
                    active: !isDynamicAllocationEnabled,
                  },
                }));
              }}
            />
            <PF2MIconButton
              size={'small'}
              variant={'filled'}
              buttonStyles={{ width: '30px', height: '30px', borderRadius: '20px' }}
              icon={<SettingsOutlinedIcon fontSize="small" />}
              onClick={() => {
                openUpdateDynamicAllocationModal(true);
                dispatch(setSelectedLoadAllocation(loadAllocation));
              }}
            />
          </div>
        </td>
      )}

      <td
        style={{
          display: 'table-cell',
          alignItems: 'center',
          backgroundColor: '#3D5465',
          borderBottom: '1px solid white',
          color: 'white',
        }}
        ref={loadDrop}
      >
        <PF2MEquipIcon
          item={loadAllocation}
          itemType={DispatchDragNDropTypes.LOAD}
          customStyles={{
            opacity: (dragItem && dragItem.obj?.equip_id === loadAllocation.equip_id) ? 0 : 1,
            width: 'unset',
          }}
          onClick={() => {
            openLoadModal(true);
            dispatch(setSelectedLoadAllocation(loadAllocation));
          }}
          tooltipData={dataExcavatorOrigins.filter(r => r.equip_id === loadAllocation.equip_id)[0]}
          // dynamicIcon={isDynamicAllocationEnabled}
          operationTypeEffective
          v2
        />
      </td>

      <td
        className={classes.scrollbars}
        style={{ width: 'inherit', backgroundColor: '#dedede' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginLeft: '10px',
            minHeight: '80px',
            flexWrap: 'wrap',
          }}
          ref={truckDrop}
        >
          {trucks && dataTrucksOrigins
            .map(truck => (
              <PF2MEquipIcon
                key={truck.equip_id}
                item={truck}
                customStyles={{
                  width: 'unset',
                  opacity: (dragItem && dragItem.obj?.equip_id === truck.equip_id) ? 0 : 1,
                }}
                itemType={DispatchDragNDropTypes.TRUCK}
                onClick={() => {
                  dispatch(setSelectedTruckAllocation(truck));
                  openTruckModal(true);
                }}
                tooltipData={trucks.find(t => t.equip_id === truck.equip_id)}
                v2
                useOperationType={truck.status === DispatchStatus.OP
                  && !((truck.operation_type === 11 || truck.operation_type === 24))
                }
              />
            ))}
        </div>
      </td>

      <td
        style={{
          display: 'table-cell',
          border: '2px solid #4a4a4a',
          whiteSpace: 'nowrap',
          paddingInline: 5,
        }}
      >
        <div className={classes.labelTypeSelector}>
          {`OP: ${trucks.filter(r => r.status === DispatchStatus.OP).length}`}
        </div>
        <div className={classes.labelTypeSelector}>
          {`PO: ${trucks.filter(r => r.status === DispatchStatus.PO).length}`}
        </div>
        <div className={classes.labelTypeSelector}>
          {`MA: ${trucks.filter(r => r.status === DispatchStatus.MA).length}`}
        </div>
      </td>
    </tr>
  );
};

AllocationRow.propTypes = {
  loadAllocation: PropTypes.object,
  loadAllocations: PropTypes.array,
  trucks: PropTypes.array,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  onTruckDrop: PropTypes.func,
  onLoadDrop: PropTypes.func,
  classes: PropTypes.object,
  openLoadModal: PropTypes.func,
  openTruckModal: PropTypes.func,
  isFullscreen: PropTypes.bool,
  dataTrucksOrigins: PropTypes.array,
  dataExcavatorOrigins: PropTypes.array,
  openUpdateDynamicAllocationModal: PropTypes.func,
  isDynamicEnable: PropTypes.bool.isRequired,
  isOverload: PropTypes.bool,
};

AllocationRow.defaultProps = {
  loadAllocation: {},
  loadAllocations: [],
  trucks: [],
  isFirst: false,
  isLast: false,
  onTruckDrop: () => null,
  onLoadDrop: () => null,
  classes: {},
  openLoadModal: () => null,
  openTruckModal: () => null,
  isFullscreen: false,
  dataTrucksOrigins: [],
  dataExcavatorOrigins: [],
  openUpdateDynamicAllocationModal: () => { },
  isOverload: false,
};

export default withStyles(styles)(AllocationRow);
